// Bundle builder on the product page (snippets/bundle-pdp-picker-dialog.liquid).
//
// "Bundle erstellen" in the bundle offer opens this sheet instead of linking to
// the bundle builder page. The sheet shows the bundle builder itself: the bundle
// section (sections/bundle.liquid) rendered for the bundle product through the
// Section Rendering API, run by the unchanged assets/bundle.js.
//
// The storefront can't be framed (X-Frame-Options: DENY), so the builder lives
// in the product page and is kept apart from it:
// - Its stylesheets and inline styles are scoped to the sheet, so global rules of
//   bundle.css (.hidden, .active, ...) don't touch the product page. The live
//   bundle page runs the A/B variant B, which the sheet always uses.
// - Its scripts run in their own scope, with document lookups limited to the
//   sheet and ?bundle=<this fragrance> as location, which preselects it.
(() => {
    if (customElements.get('bundle-pdp-picker')) return;

    const SCOPE = '.bundle-picker__content';
    const stylesheets = new Map();
    const scripts = new Map();

    function fetchText(url, cache) {
        if (!cache.has(url)) {
            cache.set(url, fetch(url).then((response) => {
                if (!response.ok) throw new Error('HTTP ' + response.status + ' ' + url);
                return response.text();
            }).catch((error) => {
                cache.delete(url);
                throw error;
            }));
        }
        return cache.get(url);
    }

    // Top-level commas only: ":is(a, b) c, d" -> [":is(a, b) c", "d"]
    function splitSelectors(selectorText) {
        const parts = [];
        let depth = 0;
        let start = 0;
        for (let i = 0; i < selectorText.length; i++) {
            const char = selectorText[i];
            if (char === '(' || char === '[') depth++;
            else if (char === ')' || char === ']') depth--;
            else if (char === ',' && depth === 0) {
                parts.push(selectorText.slice(start, i).trim());
                start = i + 1;
            }
        }
        parts.push(selectorText.slice(start).trim());
        return parts.filter(Boolean);
    }

    // Puts the sheet in front of a bundle selector. Classes the bundle page sets
    // on <html> for the A/B test move onto the sheet; other html/body qualifiers
    // (e.g. html.bundle-code-pending, set by bundle.js) stay on <html>.
    function scopeSelector(selector) {
        const match = selector.match(/^((?:html|:root|body)?(?:[.:#[][^\s>+~]*)?)(\s*[>+~]?\s*)(.*)$/s);
        const lead = match ? match[1] : '';
        const startsWithRoot = /^(html|:root|body)/.test(lead) || /^(\.bundle-ab-b|:not\(\.bundle-ab-b\))/.test(lead);
        if (!startsWithRoot) return ':where(' + SCOPE + ') ' + selector;

        let rest = match[3];
        let rootPart = lead;
        let scopePart = ':where(' + SCOPE + ')';
        rootPart = rootPart.replace(/:not\(\.bundle-ab-b\)/g, () => { scopePart += ':not(.bundle-ab-b)'; return ''; });
        rootPart = rootPart.replace(/\.bundle-ab-b/g, () => { scopePart += '.bundle-ab-b'; return ''; });
        const keepRoot = rootPart && !/^(html|:root|body)?$/.test(rootPart);
        if (!rest) return (keepRoot ? rootPart + ' ' : '') + scopePart;
        return (keepRoot ? rootPart + ' ' : '') + scopePart + ' ' + rest;
    }

    function scopeRules(rules) {
        for (const rule of rules) {
            if (rule instanceof CSSStyleRule) {
                rule.selectorText = splitSelectors(rule.selectorText).map(scopeSelector).join(', ');
            } else if (rule.cssRules && !(rule instanceof CSSKeyframesRule)) {
                scopeRules(rule.cssRules);
            }
        }
    }

    function scopeCss(cssText) {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(cssText);
        scopeRules(sheet.cssRules);
        return Array.from(sheet.cssRules, (rule) => rule.cssText).join('\n');
    }

    function styleElement(cssText) {
        const style = document.createElement('style');
        style.textContent = scopeCss(cssText);
        return style;
    }

    // Runs a bundle page script with document lookups limited to the container
    function runScoped(code, container, search) {
        const forward = (target) => ({
            get(object, property) {
                const value = Reflect.get(object, property);
                return typeof value === 'function' && !/^[A-Z]/.test(String(property)) ? value.bind(object) : value;
            },
            set(object, property, value) {
                object[property] = value;
                return true;
            }
        });

        const scopedDocument = new Proxy(document, {
            ...forward(document),
            get(object, property) {
                if (property === 'querySelector') return (selector) => container.querySelector(selector);
                if (property === 'querySelectorAll') return (selector) => container.querySelectorAll(selector);
                if (property === 'getElementById') return (id) => container.querySelector('#' + CSS.escape(id));
                return forward(document).get(object, property);
            }
        });

        const location = window.location;
        const scopedLocation = {
            href: location.origin + location.pathname + search,
            origin: location.origin,
            pathname: location.pathname,
            search,
            hash: ''
        };

        const scopedWindow = new Proxy(window, {
            ...forward(window),
            get(object, property) {
                if (property === 'location') return scopedLocation;
                if (property === 'document') return scopedDocument;
                if (property === 'addEventListener') {
                    // The page has long loaded: run load handlers right away
                    return (type, listener, options) => {
                        if (type === 'load' && document.readyState === 'complete') setTimeout(listener);
                        else window.addEventListener(type, listener, options);
                    };
                }
                return forward(window).get(object, property);
            }
        });

        new Function('document', 'window', 'location', code)(scopedDocument, scopedWindow, scopedLocation);
    }

    class BundlePdpPicker extends HTMLElement {
        connectedCallback() {
            this.dialog = this.querySelector('dialog');
            this.body = this.querySelector('[data-body]');
            this.loading = this.querySelector('[data-loading]');
            this.errorMessage = this.querySelector('[data-load-error]');
            this.builders = new Map();
            this.abortController = new AbortController();
            const { signal } = this.abortController;

            // The offer's link stays the no-JS fallback to the bundle builder page
            document.addEventListener('click', (event) => {
                const trigger = event.target.closest('[data-bundle-open]');
                if (!trigger || !this.ownsTrigger(trigger)) return;
                event.preventDefault();
                this.open(trigger.dataset.sectionUrl, trigger.dataset);
            }, { signal });

            // Choosing the bundle offer starts loading the bundle builder
            document.addEventListener('change', (event) => {
                if (event.target.value !== 'bundle' || !this.ownsTrigger(event.target)) return;
                const trigger = event.target.closest('.bundle-offer__group')?.querySelector('[data-bundle-open]');
                if (trigger) this.builderFor(trigger.dataset.sectionUrl, trigger.dataset.preselect).catch(() => {});
            }, { signal });

            // A bundle added from the sheet: the next visit starts fresh, with
            // this fragrance preselected again
            document.documentElement.addEventListener('cart:change', () => {
                if (!this.addingFrom) return;
                this.discard(this.addingFrom);
                this.addingFrom = null;
            }, { signal });

            this.dialog.addEventListener('click', (event) => {
                if (event.target === this.dialog || event.target.closest('[data-close]')) this.close();
            });

            this.dialog.addEventListener('cancel', (event) => {
                event.preventDefault();
                this.close();
            });
        }

        disconnectedCallback() {
            this.abortController.abort();
        }

        ownsTrigger(element) {
            return element.closest('bundle-offer')?.contains(this);
        }

        builderFor(url, preselect) {
            if (!this.builders.has(url)) {
                const promise = this.load(url, preselect).catch((error) => {
                    this.builders.delete(url);
                    throw error;
                });
                this.builders.set(url, promise);
            }
            return this.builders.get(url);
        }

        async load(url, preselect) {
            const html = await fetchText(url, new Map());
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const section = parsed.querySelector('.shopify-section') || parsed.body;

            const container = document.createElement('div');
            container.className = 'bundle-picker__content bundle-ab-b';
            container.hidden = true;

            // Stylesheets: the product page has theme.css already
            const linkTexts = await Promise.all(Array.from(section.querySelectorAll('link[rel="stylesheet"]'))
                .filter((link) => !/\/theme\.css/.test(link.getAttribute('href')))
                .map((link) => fetchText(new URL(link.getAttribute('href'), window.location.href).href, stylesheets)));
            section.querySelectorAll('link[rel="stylesheet"]').forEach((link) => link.remove());
            linkTexts.forEach((cssText) => container.append(styleElement(cssText)));

            section.querySelectorAll('style').forEach((style) => style.replaceWith(styleElement(style.textContent)));

            const scriptList = Array.from(section.querySelectorAll('script'));
            scriptList.forEach((script) => script.remove());

            container.append(...Array.from(section.childNodes));
            this.body.append(container);

            // Inline scripts first, then the deferred bundle.js, as on the bundle page
            const search = '?bundle=' + encodeURIComponent(preselect || '');
            const inline = scriptList.filter((script) => !script.getAttribute('src') && !script.type);
            const external = scriptList.filter((script) => script.getAttribute('src'));
            inline.forEach((script) => runScoped(script.textContent, container, search));
            for (const script of external) {
                const src = new URL(script.getAttribute('src'), window.location.href).href;
                runScoped(await fetchText(src, scripts), container, search);
            }

            this.wire(container, preselect);
            return container;
        }

        wire(container, preselect) {
            // Size switches change the bundle inside the sheet
            container.addEventListener('click', (event) => {
                const link = event.target.closest('.ml-switch a[href], a.switch-button[href]');
                if (!link) return;
                event.preventDefault();
                const url = new URL(link.getAttribute('href'), window.location.href);
                url.search = '?section_id=bundle';
                this.show(url.pathname + url.search, preselect);
            });

            // Closed before adding: apps hooking into add to cart (e.g. the upsell
            // pop-up) would otherwise open underneath the modal sheet
            const form = container.querySelector('form.bundle-form');
            form?.addEventListener('submit', () => {
                this.addingFrom = container;
                this.close();
            }, true);

            // The builder reports a failed add in its form: back to the sheet
            const error = container.querySelector('.bundle-form__error');
            if (error) {
                new MutationObserver(() => {
                    if (!error.hidden && this.addingFrom === container) {
                        this.addingFrom = null;
                        this.reopen();
                    }
                }).observe(error, { attributes: true, attributeFilter: ['hidden'] });
            }
        }

        discard(container) {
            this.builders.forEach((promise, url) => {
                promise.then((loaded) => {
                    if (loaded === container) this.builders.delete(url);
                });
            });
            container.remove();
        }

        async show(url, preselect) {
            this.activeUrl = url;
            this.loading.hidden = false;
            this.errorMessage.hidden = true;
            this.body.querySelectorAll(SCOPE).forEach((other) => { other.hidden = true; });
            try {
                const container = await this.builderFor(url, preselect);
                if (this.activeUrl !== url) return;
                container.hidden = false;
                this.body.scrollTop = 0;
            } catch (error) {
                console.error('Bundle picker:', error);
                if (this.activeUrl === url) this.errorMessage.hidden = false;
            } finally {
                if (this.activeUrl === url) this.loading.hidden = true;
            }
        }

        open(url, config) {
            this.reopen();
            this.show(url, config.preselect);
        }

        reopen() {
            this.dialog.classList.remove('is-closing');
            if (this.dialog.open) return;
            this.dialog.showModal();
            document.documentElement.classList.add('bundle-picker-open');
        }

        close() {
            if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
            this.dialog.classList.add('is-closing');
            const finish = () => {
                // reopen() during the animation cancels the closing
                if (!this.dialog.classList.contains('is-closing')) return;
                this.dialog.classList.remove('is-closing');
                this.dialog.close();
                document.documentElement.classList.remove('bundle-picker-open');
            };
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                finish();
                return;
            }
            // The backdrop's fade also ends on the dialog; wait for the sheet itself
            const onEnd = (event) => {
                if (event.target !== this.dialog || event.pseudoElement) return;
                this.dialog.removeEventListener('animationend', onEnd);
                finish();
            };
            this.dialog.addEventListener('animationend', onEnd);
        }
    }

    customElements.define('bundle-pdp-picker', BundlePdpPicker);
})();
